/**
 * Settings for Navy Phase 0 (handoff, story 30): the organization, its users,
 * the roles' permissions and the signed-in user's profile.
 *
 * There is no API behind these yet, so the model holds them itself, seeded
 * from the handoff prototype. It is created once in App.js and lives for the
 * session, as the LibraryModel does. The signed-in user is the profile and the
 * first organization user, so Profile shows who is signed in.
 */

/* Roles and capabilities as the prototype's Permissions table shows them. The
   capabilities are today's platform toggles (Settings.jsx), in sentence case,
   each with the action resapi grants for it. */
export const SETTINGS_ROLES = [
  'Analyst', 'Auditor', 'Automation operator', 'Contributor',
  'Data steward', 'Platform owner', 'Push runner', 'Reader', 'Super admin',
];

export const PERMISSION_CAPABILITIES = [
  { id: 'manage_users', label: 'Manage users', action: 'manage_users' },
  { id: 'permissions', label: 'Permissions', action: 'configure_sso' },
  { id: 'add_datasets', label: 'Add datasets', action: 'create_edit_dataset' },
  { id: 'audio_datasets', label: 'Audio datasets', action: 'record_audio_dataset' },
  { id: 'delete_datasets', label: 'Delete datasets', action: 'delete_dataset' },
  { id: 'create_connections', label: 'Create connections', action: 'create_edit_connections' },
  { id: 'create_automations', label: 'Create automations', action: 'create_edit_automations' },
  { id: 'use_ai', label: 'Use AI / CodaAI', action: 'use_ai' },
  { id: 'saved_queries', label: 'Saved queries', action: 'save_queries_views' },
  { id: 'view_audit_logs', label: 'View audit logs', action: 'view_audit_logs' },
];

// Which roles hold each capability, in SETTINGS_ROLES order (the prototype's seed).
const SEED_GRANTS = {
  manage_users: [0, 0, 0, 0, 0, 1, 1, 0, 1],
  permissions: [0, 0, 0, 0, 0, 1, 0, 1, 1],
  add_datasets: [1, 0, 0, 1, 0, 1, 1, 0, 1],
  audio_datasets: [1, 0, 0, 0, 0, 0, 1, 1, 1],
  delete_datasets: [1, 0, 0, 0, 0, 1, 0, 1, 1],
  create_connections: [1, 0, 0, 1, 0, 1, 0, 0, 1],
  create_automations: [1, 0, 0, 0, 0, 1, 0, 0, 1],
  use_ai: [1, 0, 0, 1, 0, 1, 0, 0, 1],
  saved_queries: [1, 0, 0, 1, 0, 1, 0, 0, 1],
  view_audit_logs: [0, 1, 0, 0, 1, 1, 0, 0, 0],
};

/** { capability id: { role: held } } */
export function seedPermissions() {
  return Object.fromEntries(PERMISSION_CAPABILITIES.map(({ id }) => [
    id,
    Object.fromEntries(SETTINGS_ROLES.map((role, i) => [role, SEED_GRANTS[id][i] === 1])),
  ]));
}

/** How many role and capability pairs differ between two grids. */
export function permissionChanges(saved, draft) {
  return PERMISSION_CAPABILITIES.reduce(
    (n, { id }) => n + SETTINGS_ROLES.filter((role) => !!saved[id]?.[role] !== !!draft[id]?.[role]).length,
    0,
  );
}

export const USER_STATUSES = ['Active', 'Disabled'];

/* The prototype's choices for the organization form. */
export const ORG_TIME_ZONES = ['America/Halifax', 'America/St_Johns', 'America/Toronto', 'America/Winnipeg', 'America/Vancouver', 'UTC'];
export const ORG_DATA_REGIONS = ['Canada', 'United States', 'United Kingdom', 'Australia'];
export const ORG_SESSION_TIMEOUTS = ['15 minutes', '30 minutes', '60 minutes', '8 hours'];

/* The five fills of the brand mark, plus white and black, named by their hex
   as the prototype names them. The initials take whichever of black or white
   contrasts more with the fill. */
export const AVATAR_COLOURS = [
  { id: '#d9e8e6', fill: 'var(--color-brand-tertiary-100)', ink: 'var(--color-typography-text-black)' },
  { id: '#1fbcbc', fill: 'var(--color-brand-secondary-500)', ink: 'var(--color-typography-text-black)' },
  { id: '#00deff', fill: 'var(--color-brand-quinary-400)', ink: 'var(--color-typography-text-black)' },
  { id: '#135eff', fill: 'var(--color-brand-primary-600)', ink: 'var(--color-typography-text-white)' },
  { id: '#233146', fill: 'var(--color-surface-brand)', ink: 'var(--color-typography-text-white)' },
  { id: '#ffffff', fill: 'var(--color-surface-white)', ink: 'var(--color-typography-text-black)' },
  { id: '#1a1919', fill: 'var(--color-surface-black)', ink: 'var(--color-typography-text-white)' },
];

export const avatarColour = (id) => AVATAR_COLOURS.find((c) => c.id === id) || AVATAR_COLOURS[0];

/** Up to two initials from a display name. */
export const initialsOf = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();

/* A fictional persona, the one the prototype and the Library handoff carry,
   for when nobody is signed in (Storybook). The rest are the prototype's demo
   users, on reserved example domains. */
const PERSONA = { name: 'Jordan Ellis', email: 'jordan.ellis@example.com', role: 'Platform owner' };

const DEMO_USERS = [
  { id: 'user2', name: 'LCdr M. Arsenault', email: 'm.arsenault@navy.example', role: 'Data steward', status: 'Active' },
  { id: 'user3', name: 'PO1 S. Okonkwo', email: 's.okonkwo@navy.example', role: 'Contributor', status: 'Active' },
  { id: 'user4', name: 'Lt(N) R. Beaumont', email: 'r.beaumont@navy.example', role: 'Contributor', status: 'Active' },
  { id: 'user5', name: 'CPO2 T. Nakamura', email: 't.nakamura@navy.example', role: 'Analyst', status: 'Active' },
];

// The signed-in user as the platform profile names them, with the persona's
// details where the profile has none. A profile role outside this list (the
// platform's "Owner") reads as Platform owner, the prototype's word for it.
function signedInUser(me) {
  const role = SETTINGS_ROLES.find((r) => r.toLowerCase() === String(me?.role_name || '').toLowerCase());
  return {
    id: me?.id != null ? String(me.id) : 'user1',
    name: me?.name && !me.name.includes('@') ? me.name : (me?.email ? me.email.split('@')[0] : PERSONA.name),
    email: me?.email || PERSONA.email,
    role: role || PERSONA.role,
    status: 'Active',
  };
}

export function seedSettingsUsers(me) {
  return [signedInUser(me), ...DEMO_USERS.map((u) => ({ ...u }))];
}

export function seedOrganization(me) {
  return {
    name: me?.org_display_name || me?.org_name || 'Royal Canadian Navy',
    timeZone: 'America/Halifax',
    dataRegion: 'Canada',
    sessionTimeout: '30 minutes',
    rememberEmail: true,
  };
}

export function seedProfile(me) {
  const user = signedInUser(me);
  return {
    userId: user.id,
    displayName: user.name,
    title: 'Workforce Planning Lead',
    department: 'Sea Training',
    about: '',
    email: user.email,
    colour: AVATAR_COLOURS[0].id,
    teams: [],
    reviewers: [],
  };
}

const plainRows = (rows) => rows.map((r) => ({ name: r.name, email: r.email }));
const cloneTeams = (teams) => teams.map((t) => ({ name: t.name, members: plainRows(t.members) }));

export class SettingsModel {
  constructor(me = null) {
    this.users = seedSettingsUsers(me);
    this.userSort = { key: 'name', dir: 'asc' }; // Name, A to Z
    this.permissions = seedPermissions();
    this.permissionSort = { key: 'role', dir: 'asc' }; // Role, A to Z
    this.organization = seedOrganization(me);
    this.profile = seedProfile(me);
    this.listeners = [];
    this.nextId = 1;
  }

  setUserSort(sort) {
    this.userSort = sort;
    this.notify();
  }

  setPermissionSort(sort) {
    this.permissionSort = sort;
    this.notify();
  }

  /** Adds a user with every field. Returns the added user, or null. */
  addUser({ name, email, role, status }) {
    const user = { id: `usr${this.nextId++}`, name: name.trim(), email: email.trim(), role, status };
    if (!user.name || !user.email || !SETTINGS_ROLES.includes(role) || !USER_STATUSES.includes(status)) return null;
    this.users = [...this.users, user];
    this.notify();
    return user;
  }

  /** Only users are removed; everything else is archived. Returns the removed user, or null. */
  removeUser(id) {
    const user = this.users.find((u) => u.id === id);
    if (!user) return null;
    this.users = this.users.filter((u) => u.id !== id);
    this.notify();
    return user;
  }

  /** Commits a whole permissions grid. Returns how many pairs changed. */
  savePermissions(draft) {
    const changed = permissionChanges(this.permissions, draft);
    if (!changed) return 0;
    this.permissions = Object.fromEntries(PERMISSION_CAPABILITIES.map(({ id }) => [id, { ...draft[id] }]));
    this.notify();
    return changed;
  }

  /** The name is required; an empty one keeps the saved name. */
  saveOrganization(fields) {
    this.organization = { ...this.organization, ...fields, name: fields.name.trim() || this.organization.name };
    this.notify();
    return this.organization;
  }

  saveProfile(fields) {
    const displayName = fields.displayName.trim() || this.profile.displayName;
    this.profile = {
      ...this.profile,
      ...fields,
      displayName,
      title: fields.title.trim(),
      department: fields.department.trim(),
      teams: cloneTeams(fields.teams),
      reviewers: plainRows(fields.reviewers),
    };
    // The profile's user carries the same name.
    this.users = this.users.map((u) => (u.id === this.profile.userId ? { ...u, name: displayName } : u));
    this.notify();
    return this.profile;
  }

  /** The signed-in user's role, as Users shows it. */
  profileRole() {
    return this.users.find((u) => u.id === this.profile.userId)?.role || '';
  }

  getState() {
    return {
      users: this.users,
      userSort: this.userSort,
      permissions: this.permissions,
      permissionSort: this.permissionSort,
      organization: this.organization,
      profile: this.profile,
      profileRole: this.profileRole(),
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
}
